import i18n from '../services/i18n/i18n';

/**
 * Instants renvoyés par l'API (datetime Python naïf) : souvent en UTC, sérialisés
 * en ISO **sans** `Z`. `new Date("...T00:00:00")` est alors interprété en **heure locale**,
 * ce qui décale l'affichage d'une ou plusieurs heures vs l'horloge du système.
 * Si la chaîne a déjà un fuseau (Z ou ±hh:mm), on garde le parseur natif.
 */
export const parseServerInstant = (
    input: string | Date | null | undefined
): Date => {
    if (input == null || input === '') {
        return new Date(NaN);
    }
    if (input instanceof Date) {
        return input;
    }
    const s0 = String(input).trim();
    if (s0 === '') {
        return new Date(NaN);
    }
    if (/Z$/i.test(s0) || /[+\-][0-9]{2}:[0-9]{2}$/.test(s0)) {
        return new Date(s0);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s0)) {
        return new Date(s0);
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(s0)) {
        return new Date(`${s0}Z`);
    }
    return new Date(s0);
};

/**
 * Obtient la locale appropriée selon la langue actuelle de l'application
 */
export const getCurrentLocale = (): string => {
    return i18n.language === 'fr' ? 'fr-FR' : 'en-US';
};

/**
 * Formate une date selon la locale actuelle de l'application
 */
export const formatDate = (
    dateString: string | Date,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }
): string => {
    if (!dateString) return "";

    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString(getCurrentLocale(), options);
};

/**
 * Formate une date avec l'heure selon la locale actuelle
 */
export const formatDateTime = (
    dateString: string | Date | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }
): string => {
    if (!dateString) return "N/A";

    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString(getCurrentLocale(), options);
};

const isSameCalendarDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const timeOptionsForLocale = (locale: string): Intl.DateTimeFormatOptions => ({
    hour: '2-digit',
    minute: '2-digit',
    hour12: !locale.startsWith('fr'),
});

/**
 * Formate l'heure d'un message selon les règles de l'interface chat
 * - Si le message est le même jour que `referenceDay` (par défaut : aujourd'hui) : seulement l'heure
 * - Sinon : jour + mois abrégé + année, puis heure (ex. 28 avr. 2026 · 14:30)
 */
export const formatChatTimestamp = (
    dateString: string | Date | null | undefined,
    referenceDay: Date = new Date()
): string => {
    if (!dateString) return "";

    const date = typeof dateString === 'string' ? parseServerInstant(dateString) : dateString;

    if (isNaN(date.getTime())) return "";

    const locale = getCurrentLocale();

    if (isSameCalendarDay(date, referenceDay)) {
        return date.toLocaleTimeString(locale, timeOptionsForLocale(locale));
    }

    const datePart = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    const timePart = date.toLocaleTimeString(locale, timeOptionsForLocale(locale));
    return `${datePart} · ${timePart}`;
};

/**
 * Libellé complet pour la barre de date au scroll : jour, mois abrégé, année et heure
 */
export const formatInterviewScrollHeader = (
    dateString: string | Date | null | undefined
): string => {
    if (!dateString) return "";

    const date = typeof dateString === 'string' ? parseServerInstant(dateString) : dateString;

    if (isNaN(date.getTime())) return "";

    const locale = getCurrentLocale();
    const datePart = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    const timePart = date.toLocaleTimeString(locale, timeOptionsForLocale(locale));
    return `${datePart} · ${timePart}`;
};

/**
 * Infobulle au survol : date/heure complète (même format que le bandeau scroll),
 * uniquement quand l’affichage compact « heure seule » est utilisé (même jour que `referenceDay`).
 */
export const getChatTimestampHoverTitle = (
    dateString: string | Date | null | undefined,
    referenceDay: Date = new Date()
): string | undefined => {
    if (!dateString) return undefined;
    const date = typeof dateString === 'string' ? parseServerInstant(dateString) : dateString;
    if (isNaN(date.getTime())) return undefined;
    if (!isSameCalendarDay(date, referenceDay)) return undefined;
    return formatInterviewScrollHeader(date);
};

/**
 * Libellé relatif pour le fil « Collect data » (now → secondes → minutes → heures → heure → date).
 * `title` est toujours la date/heure complète pour l’infobulle.
 */
export const formatCollectWorkspaceRelativeLabel = (
    dateInput: string | Date | null | undefined,
    referenceNow: Date = new Date()
): { label: string; title: string } => {
    const tk = 'form.collectWorkspace.messageTime';
    const t = (key: string, opt?: Record<string, unknown>) => i18n.t(key, opt);

    if (!dateInput) return { label: '', title: '' };

    const date = typeof dateInput === 'string' ? parseServerInstant(dateInput) : dateInput;
    if (isNaN(date.getTime())) return { label: '', title: '' };

    const title = formatInterviewScrollHeader(date);
    const diffMs = referenceNow.getTime() - date.getTime();
    if (diffMs < 0) {
        return { label: date.toLocaleTimeString(getCurrentLocale(), timeOptionsForLocale(getCurrentLocale())), title };
    }

    const secTotal = Math.floor(diffMs / 1000);
    if (secTotal < 20) {
        return { label: t(`${tk}.now`), title };
    }
    if (secTotal < 60) {
        return { label: t(`${tk}.seconds`, { count: secTotal }), title };
    }
    const minTotal = Math.floor(secTotal / 60);
    if (minTotal < 60) {
        return { label: t(`${tk}.minutes`, { count: minTotal }), title };
    }
    const hrTotal = Math.floor(minTotal / 60);
    if (hrTotal < 24) {
        return { label: t(`${tk}.hours`, { count: hrTotal }), title };
    }

    const locale = getCurrentLocale();
    if (isSameCalendarDay(date, referenceNow)) {
        return {
            label: date.toLocaleTimeString(locale, timeOptionsForLocale(locale)),
            title,
        };
    }

    const y = new Date(referenceNow);
    y.setDate(y.getDate() - 1);
    if (isSameCalendarDay(date, y)) {
        const timePart = date.toLocaleTimeString(locale, timeOptionsForLocale(locale));
        return { label: t(`${tk}.yesterday`, { time: timePart }), title };
    }

    if (date.getFullYear() === referenceNow.getFullYear()) {
        const shortDate = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
        const timePart = date.toLocaleTimeString(locale, timeOptionsForLocale(locale));
        return { label: `${shortDate} · ${timePart}`, title };
    }

    return { label: formatInterviewScrollHeader(date), title };
};

/**
 * Formate une date courte (ex: 12/01/2026 ou 01/12/2026)
 */
export const formatShortDate = (dateString: string | Date): string => {
    return formatDate(dateString, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

/**
 * Date/heure de dernière modification pour cartes processus (Home, bibliothèque, etc.)
 * Accepte ISO string, timestamp ms (nombre) ou Date.
 */
export const formatProcessCardUpdatedAt = (
    dateInput: string | Date | number | null | undefined
): string => {
    if (dateInput === null || dateInput === undefined || dateInput === '') return '';
    const date =
        typeof dateInput === 'string' ? parseServerInstant(dateInput) : new Date(dateInput as number | Date);
    if (isNaN(date.getTime())) return '';
    const locale = getCurrentLocale();
    return date.toLocaleString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * Dernière date connue pour un processus (API snake_case ou camelCase, sinon création).
 */
export const getProcessModifiedTimestamp = (p: Record<string, unknown> | null | undefined): string | number | undefined => {
    if (!p) return undefined;
    const u = p['updated_at'] ?? p['updatedAt'];
    const c = p['created_at'] ?? p['createdAt'];
    if (u !== undefined && u !== null && String(u) !== '') return u as string | number;
    if (c !== undefined && c !== null && String(c) !== '') return c as string | number;
    return undefined;
};

/**
 * Formate une date longue (ex: 12 janvier 2026 ou January 12, 2026)
 */
export const formatLongDate = (dateString: string | Date): string => {
    return formatDate(dateString, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Formate un mois et année (ex: janvier 2026 ou January 2026)
 */
export const formatMonthYear = (dateString: string | Date): string => {
    return formatDate(dateString, {
        year: 'numeric',
        month: 'long'
    });
};

/**
 * Formate un nombre selon la locale actuelle de l'application
 */
export const formatNumber = (
    number: number,
    options: Intl.NumberFormatOptions = {}
): string => {
    if (typeof number !== 'number' || isNaN(number)) return '0';
    
    return number.toLocaleString(getCurrentLocale(), options);
};

/**
 * Fonction legacy pour la compatibilité (format technique)
 */
const formatDateTimeLegacy = (dateString: string) => {
    if (!dateString) {
        return "";
    }

    const date = parseServerInstant(dateString);

    if (isNaN(date.getTime())) {
        return "";
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Exports
export default formatDateTimeLegacy;